import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import useContainerWidth from '@/components/useContainerWidth';
import { TrendingDown, ChevronRight } from 'lucide-react';
import QualityNarrativePanel from '@/components/QualityNarrativePanel';
import ReitCashLensPanel from '@/components/ReitCashLensPanel';
import AnalystCoverageCard from '@/components/AnalystCoverageCard';
import ValuationMultiplesCard from '@/components/ValuationMultiplesCard';
import AbbrTip from '@/components/AbbrTip';
import { EtfTopHoldingsCard } from '@/components/EtfTopHoldingsCard';
import { HeldByEtfsCard } from '@/components/HeldByEtfsCard';
import { unusualVolumeBadge } from '@/lib/volume';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Round P130 (Feb 2026) — In-memory cache for price-history +
// indicator-series per (ticker, period). Prevents the entire tab
// from tearing down on every timeline switch: when a user has
// already viewed 1yr and 6mo in a single Stock Modal session, going
// back to either is instant (no fetch, no draw animation, no
// unmount of ReitCashLensPanel + cards above/below the chart).
//
// The cache is cleared when the Stock Modal fires `stock-modal-closed`
// — so re-opening the same ticker later gets a fresh fetch (in
// case the underlying data moved). Bounded to 100 total entries to
// bound memory during heavy screener-hopping.
const _priceHistoryCache = new Map(); // key: `${ticker}:${period}` → { data, cachedAt }
const _indicatorSeriesCache = new Map(); // key: `${ticker}:${indicatorKey}:${period}` → dateMap
const PRICE_CACHE_MAX = 100;
const INDICATOR_CACHE_MAX = 300;

function _lruSet(cache, key, val, maxSize) {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, val);
  while (cache.size > maxSize) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
}

// Global reset — invoked when the Stock Modal closes. We listen on
// `window` for the `stock-modal-closed` custom event so this module
// doesn't need a React context.
if (typeof window !== 'undefined' && !window.__stockMetricsCacheListenerInstalled) {
  window.addEventListener('stock-modal-closed', () => {
    _priceHistoryCache.clear();
    _indicatorSeriesCache.clear();
  });
  window.__stockMetricsCacheListenerInstalled = true;
}

const PERIODS = [
  { key: '1mo', label: '1mo' },
  { key: '3mo', label: '3mo' },
  { key: '6mo', label: '6mo' },
  { key: '1y', label: '1y' },
  { key: '5y', label: '5y' },
  { key: 'max', label: 'max' },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatVolume(vol) {
  if (!vol) return '--';
  if (vol >= 1_000_000_000) return `${(vol / 1_000_000_000).toFixed(2)}B`;
  if (vol >= 1_000_000) return `${(vol / 1_000_000).toFixed(2)}M`;
  if (vol >= 1_000) return `${(vol / 1_000).toFixed(1)}K`;
  return vol.toLocaleString();
}

function formatMoney(val) {
  if (val == null) return '--';
  if (Math.abs(val) >= 1e12) return `$${(val / 1e12).toFixed(2)}T`;
  if (Math.abs(val) >= 1e9) return `$${(val / 1e9).toFixed(2)}B`;
  if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
  return `$${val.toLocaleString()}`;
}

function isFiniteValue(val) {
  // Returns true only for real finite numbers. Rejects null, undefined,
  // string 'NaN', NaN, Infinity. Used to gate conditional rendering of
  // numeric metric rows so a "NaN%" never reaches the user.
  if (val == null) return false;
  const n = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(n);
}

// Round 91z'' — backend serves `holdings_valuation` as a free-form
// dict (e.g. {"P/E": 16.78, "P/B": 1.91, "P/S": 1.49, "P/CF": 10.48}).
// Map those string labels to AbbrTip defKeys so the Portfolio Valuation
// section also gets the info-popover treatment.
const PORTFOLIO_VAL_DEFKEY = {
  'P/E': 'TRAILING_PE',
  'P/B': 'PRICE_BOOK',
  'P/S': 'PRICE_SALES',
  'P/CF': 'PRICE_CASHFLOW',
  'Price/Sales': 'PRICE_SALES',
  'Price/Cash Flow': 'PRICE_CASHFLOW',
};


function formatPct(val) {
  if (!isFiniteValue(val)) return '--';
  const n = typeof val === 'number' ? val : Number(val);
  return `${(n * 100).toFixed(2)}%`;
}

function formatNum(val, decimals = 2) {
  if (!isFiniteValue(val)) return '--';
  const n = typeof val === 'number' ? val : Number(val);
  return n.toFixed(decimals);
}

function formatDateLabel(dateStr, period) {
  const d = new Date(dateStr + 'T00:00:00');
  const mon = MONTHS[d.getMonth()];
  const day = d.getDate();
  const yr2 = String(d.getFullYear()).slice(2);
  if (period === '1mo' || period === '3mo') return `${mon} ${day}`;
  if (period === '6mo' || period === '1y' || period === '5y') return `${mon} ${yr2}`;
  return `${d.getFullYear()}`;
}

function formatTooltipDate(dateStr, period) {
  const d = new Date(dateStr + 'T00:00:00');
  const mon = MONTHS[d.getMonth()];
  const day = d.getDate();
  const yr2 = String(d.getFullYear()).slice(2);
  if (period === '1mo' || period === '3mo') return `${mon} ${day}, '${yr2}`;
  return `${mon} '${yr2}`;
}

function PriceChart({ prices, changePct, period, overlays = [], crossovers = [], animKey = 0 }) {
  const containerRef = useRef(null);
  const measuredWidth = useContainerWidth(containerRef);
  const pathRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  // Round 102c (Feb 2026) — Tap/click popover for Golden/Death cross
  // markers. Phone view has no hover, so we surface the same
  // explanation as an on-tap card. Desktop clicks work identically —
  // one interaction pattern across every device.
  const [activeCross, setActiveCross] = useState(null);

  // Round P130 (Feb 2026) — Left-to-right draw-in animation.
  // Whenever the parent bumps `animKey` (fresh price-history fetch),
  // measure the path length and animate stroke-dashoffset from
  // that length down to zero over ~1.8 s. On cache-hit renders the
  // key doesn't change → no animation → chart pops in instantly.
  useEffect(() => {
    const el = pathRef.current;
    if (!el) return undefined;
    try {
      const len = el.getTotalLength();
      if (!Number.isFinite(len) || len <= 0) return undefined;
      // Kick off from fully-dashed state, then transition to 0.
      el.style.transition = 'none';
      el.style.strokeDasharray = `${len}`;
      el.style.strokeDashoffset = `${len}`;
      // Next frame — apply the transition and set dashoffset to 0.
      requestAnimationFrame(() => {
        el.style.transition = 'stroke-dashoffset 1.8s ease-out';
        el.style.strokeDashoffset = '0';
      });
    } catch (_e) { /* getTotalLength unsupported in test envs */ }
    return undefined;
  // measuredWidth: the path gets longer when the container widens (e.g. cached
  // prices on a resized modal) — re-measure so the dash length matches.
  }, [animKey, prices, measuredWidth]);

  // Dismiss the crossover card on outside-click / Esc.
  useEffect(() => {
    if (!activeCross) return undefined;
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) setActiveCross(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setActiveCross(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [activeCross]);

  const chartData = useMemo(() => {
    if (!prices || prices.length < 2) return null;
    const closes = prices.map(p => p.close);
    // Include overlay values in min/max so SMA lines aren't clipped off-chart.
    const overlayValues = (overlays || []).flatMap(o => (o.dateMap ? Object.values(o.dateMap) : [])).filter(v => v != null && Number.isFinite(v));
    const allVals = [...closes, ...overlayValues];
    const minPrice = Math.min(...allVals);
    const maxPrice = Math.max(...allVals);
    const range = maxPrice - minPrice || 1;
    const width = measuredWidth;
    const stagger = width < 600;
    const height = stagger ? 232 : 220;
    const padLeft = 60, padRight = 10, padTop = 10, padBottom = stagger ? 42 : 30;
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;
    const points = prices.map((p, i) => ({
      x: padLeft + (i / (prices.length - 1)) * chartW,
      y: padTop + chartH - ((p.close - minPrice) / range) * chartH,
      ...p,
    }));
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    // Build overlay paths by mapping each price date to its overlay value.
    const overlayPaths = (overlays || []).map((o) => {
      if (!o.dateMap) return null;
      const segs = [];
      let cmd = 'M';
      prices.forEach((p, i) => {
        const v = o.dateMap[p.date];
        if (v == null || !Number.isFinite(v)) {
          cmd = 'M'; // gap — restart line
          return;
        }
        const x = padLeft + (i / (prices.length - 1)) * chartW;
        const y = padTop + chartH - ((v - minPrice) / range) * chartH;
        segs.push(`${cmd} ${x} ${y}`);
        cmd = 'L';
      });
      return { ...o, d: segs.join(' ') };
    }).filter(Boolean);
    const yTicks = [];
    for (let i = 0; i <= 4; i++) {
      const val = minPrice + (range * i) / 4;
      yTicks.push({ val: `$${val.toFixed(2)}`, y: padTop + chartH - (i / 4) * chartH });
    }
    const xTicks = [];
    const step = Math.max(1, Math.floor(prices.length / 6));
    for (let i = 0; i < prices.length; i += step) {
      xTicks.push({ label: formatDateLabel(prices[i].date, period), x: padLeft + (i / (prices.length - 1)) * chartW });
    }
    const lastLabel = formatDateLabel(prices[prices.length - 1].date, period);
    if (!xTicks.length || xTicks[xTicks.length - 1].label !== lastLabel) {
      xTicks.push({ label: lastLabel, x: padLeft + chartW });
    }
    // Anchor the final tick to the right edge of the plot area so the
    // last date (e.g. "Oct 12") doesn't bleed past the chart and get
    // clipped by the sidebar. Mirror on the left so the first label
    // doesn't collide with the Y-axis values either.
    xTicks.forEach((t) => { t.anchor = 'middle'; });
    if (xTicks.length) {
      xTicks[0].anchor = 'start';
      xTicks[xTicks.length - 1].anchor = 'end';
    }
    // Phone view: alternate labels between two rows (top / bottom / top …)
    // so all dates stay readable without colliding.
    xTicks.forEach((t, i) => { t.y = height - 5 - (stagger && i % 2 === 0 ? 11 : 0); });
    // Round 102b (Feb 2026) — Golden/Death cross markers. Each crossover
    // is anchored to a price-series index (the day the SMA50 crossed
    // SMA200 in either direction), then rendered as a small triangle
    // above the price line so it doesn't collide with the SMA200 curve.
    const dateToIdx = new Map();
    prices.forEach((p, i) => { dateToIdx.set(p.date, i); });
    const crossoverPoints = (crossovers || [])
      .map((c) => {
        const idx = dateToIdx.get(c.date);
        if (idx == null) return null;
        return {
          kind: c.kind,
          date: c.date,
          x: padLeft + (idx / (prices.length - 1)) * chartW,
          y: points[idx].y,
        };
      })
      .filter(Boolean);
    return { points, pathD, overlayPaths, yTicks, xTicks, width, height, padLeft, padTop, padBottom, chartW, chartH, isPositive: changePct >= 0, crossoverPoints };
  }, [prices, changePct, period, overlays, crossovers, measuredWidth]);

  const handleMouseMove = useCallback((e) => {
    if (!chartData || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * chartData.width;
    let closest = null, closestDist = Infinity;
    for (const p of chartData.points) {
      const dist = Math.abs(p.x - svgX);
      if (dist < closestDist) { closestDist = dist; closest = p; }
    }
    if (closest && closestDist < 30) {
      setTooltip({
        date: formatTooltipDate(closest.date, period), price: closest.close,
        svgX: closest.x, svgY: closest.y,
        screenX: (closest.x / chartData.width) * rect.width,
        screenY: (closest.y / chartData.height) * rect.height,
      });
    } else setTooltip(null);
  }, [chartData, period]);

  if (!chartData) return <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No chart data available</div>;
  const lineColor = chartData.isPositive ? '#22c55e' : '#ef4444';

  return (
    <div ref={containerRef} className="relative w-full overflow-x-auto" onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)} data-testid="price-chart-svg">
      <svg viewBox={`0 0 ${chartData.width} ${chartData.height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {chartData.yTicks.map((t, i) => <line key={i} x1={chartData.padLeft} y1={t.y} x2={chartData.padLeft + chartData.chartW} y2={t.y} className="stroke-border/30" strokeDasharray="3,3" />)}
        {chartData.yTicks.map((t, i) => <text key={`yl-${i}`} x={chartData.padLeft - 5} y={t.y + 3} textAnchor="end" className="fill-muted-foreground" fontSize="9" fontFamily="IBM Plex Mono, monospace">{t.val}</text>)}
        {chartData.xTicks.map((t, i) => <text key={`xl-${i}`} x={t.x} y={t.y} textAnchor={t.anchor || 'middle'} className="fill-muted-foreground" fontSize="9" fontFamily="IBM Plex Mono, monospace">{t.label}</text>)}
        <path ref={pathRef} d={chartData.pathD} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {chartData.overlayPaths?.map((o) => (
          <path
            key={o.key}
            d={o.d}
            fill="none"
            stroke={o.color}
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            strokeDasharray={o.dashed ? '5,3' : undefined}
            opacity="0.85"
            data-testid={`chart-overlay-${o.key}`}
          />
        ))}
        {/* Round 102b (Feb 2026) — Golden/Death cross markers. Green ▲
            = golden cross (SMA50 crossed ABOVE SMA200 — bullish trend
            regime). Red ▼ = death cross (SMA50 crossed BELOW SMA200 —
            bearish). Rendered above/below the price line for
            legibility. Only visible when both SMA50 and SMA200 are
            active overlays. */}
        {chartData.crossoverPoints?.map((c, i) => {
          const isGolden = c.kind === 'golden';
          const fill = isGolden ? '#10b981' : '#e11d48';
          const size = 6;
          // Golden = triangle pointing up, ABOVE price line (y - 12)
          // Death  = triangle pointing down, BELOW price line (y + 12)
          const anchorY = isGolden ? c.y - 14 : c.y + 14;
          const pathD = isGolden
            ? `M ${c.x - size} ${anchorY + size / 2} L ${c.x + size} ${anchorY + size / 2} L ${c.x} ${anchorY - size} Z`
            : `M ${c.x - size} ${anchorY - size / 2} L ${c.x + size} ${anchorY - size / 2} L ${c.x} ${anchorY + size} Z`;
          const isActive = activeCross && activeCross.date === c.date && activeCross.kind === c.kind;
          return (
            <g
              key={`cross-${i}`}
              onClick={(e) => {
                e.stopPropagation();
                // Screen-space position for the HTML popover — derived
                // from the SVG anchor + the container's current width
                // (SVG scales, so we normalise via viewBox ratio).
                const rect = containerRef.current?.getBoundingClientRect();
                const scaleX = (rect?.width || chartData.width) / chartData.width;
                const scaleY = (rect?.height || chartData.height) / chartData.height;
                setActiveCross({
                  ...c,
                  screenX: c.x * scaleX,
                  screenY: anchorY * scaleY,
                });
              }}
              style={{ cursor: 'pointer' }}
              data-testid={`chart-crossover-${c.kind}-${c.date}`}
            >
              {/* Invisible, larger hit target so thumbs can land on it. */}
              <circle cx={c.x} cy={anchorY} r="12" fill="transparent" />
              <path
                d={pathD}
                fill={fill}
                stroke={isActive ? '#facc15' : 'white'}
                strokeWidth={isActive ? '2' : '1'}
                opacity="0.95"
              />
            </g>
          );
        })}
        {tooltip && <>
          <line x1={tooltip.svgX} y1={chartData.padTop} x2={tooltip.svgX} y2={chartData.padTop + chartData.chartH} stroke="#666" strokeWidth="1" strokeDasharray="4,2" />
          <circle cx={tooltip.svgX} cy={tooltip.svgY} r="4" fill={lineColor} stroke="white" strokeWidth="1.5" />
        </>}
      </svg>
      {tooltip && (
        <div className="absolute z-10 pointer-events-none bg-card border rounded-md shadow-lg px-3 py-2 text-xs"
          style={{ left: `${Math.min(tooltip.screenX + 12, (containerRef.current?.clientWidth || 500) - 120)}px`, top: `${Math.max(tooltip.screenY - 25, 0)}px` }}
          data-testid="price-chart-tooltip">
          <p className="font-semibold text-foreground">{tooltip.date}</p>
          <p><span className="text-muted-foreground">Price:</span> <span className="font-mono font-medium">${tooltip.price.toFixed(2)}</span></p>
        </div>
      )}
      {/* Round 102c (Feb 2026) — Tap-to-explain popover for the
          Golden/Death cross triangles. Desktop hover on SVG `<title>`
          was inconsistent across browsers and completely absent on
          phone, so a click-to-open card replaces it on every device. */}
      {activeCross && (() => {
        const isGolden = activeCross.kind === 'golden';
        const w = containerRef.current?.clientWidth || 500;
        // Anchor near the marker but keep the card fully on-screen.
        const left = Math.max(8, Math.min(activeCross.screenX - 120, w - 250));
        const top = isGolden ? Math.max(4, activeCross.screenY - 90) : activeCross.screenY + 12;
        return (
          <div
            className={`absolute z-20 bg-card border rounded-md shadow-lg px-3 py-2 text-xs w-[240px] ${isGolden ? 'border-emerald-500/50' : 'border-rose-500/50'}`}
            style={{ left: `${left}px`, top: `${top}px` }}
            data-testid={`crossover-popover-${activeCross.kind}`}
            role="dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide ${isGolden ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                <span>{isGolden ? '▲' : '▼'}</span>
                {isGolden ? 'Golden Cross' : 'Death Cross'}
              </span>
              <button
                type="button"
                onClick={() => setActiveCross(null)}
                className="text-muted-foreground hover:text-foreground text-sm leading-none px-1"
                aria-label="Close"
                data-testid="crossover-popover-close"
              >
                ×
              </button>
            </div>
            <p className="font-mono text-[11px] text-muted-foreground mb-1.5">{activeCross.date}</p>
            <p className="text-[11px] leading-snug text-foreground/90">
              {isGolden
                ? 'The 50-day moving average crossed ABOVE the 200-day. Historically read as the start of a bullish trend regime — the short-term average is rising faster than the long-term baseline, and momentum is turning positive.'
                : 'The 50-day moving average crossed BELOW the 200-day. Historically read as the start of a bearish trend regime — recent prices are dragging the short-term average under the long-term baseline, and momentum is turning negative.'}
            </p>
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Signal only — not a buy/sell recommendation. Best used alongside earnings, dividend safety, and macro context.
            </p>
          </div>
        );
      })()}
    </div>
  );
}

function MetricRow({ label, value, testId, defKey }) {
  // Round 91z — if `defKey` is provided, wrap the label in an AbbrTip
  // so a tap reveals the definition + a deep link to the matching
  // Glossary entry. All defKeys live in AbbrTip's DEFINITIONS table.
  // No defKey = plain text (legacy path, still works).
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border/30 last:border-0" data-testid={testId}>
      <span className="text-xs font-bold text-muted-foreground">
        {defKey ? <AbbrTip term={defKey}>{label}</AbbrTip> : label}
      </span>
      <span className="text-xs font-mono font-semibold text-foreground">{value}</span>
    </div>
  );
}

// Round 95n (Jun 2026) — Seeking-Alpha-style range slider. Gradient
// track with a dot marking where `current` sits between `low` and
// `high`. `current` is fed the LIVE streamed price from the modal's
// SSE subscription, so the dot drifts in real time during market
// hours. Renders nothing when bounds are missing/degenerate.
// Round 95o — brokerage-terminal tick pulse: the dot (and price
// label) flash green/red for ~700ms on every live tick, keyed to
// price direction.
function RangeBar({ label, low, high, current, testId }) {
  const prevRef = useRef(null);
  const [flash, setFlash] = useState(null);
  const cur = isFiniteValue(current) ? Number(current) : null;
  useEffect(() => {
    if (cur == null) return undefined;
    const prev = prevRef.current;
    prevRef.current = cur;
    if (prev == null || cur === prev) return undefined;
    setFlash(cur > prev ? 'up' : 'down');
    const id = setTimeout(() => setFlash(null), 700);
    return () => clearTimeout(id);
  }, [cur]);
  if (!isFiniteValue(low) || !isFiniteValue(high) || Number(high) <= Number(low)) return null;
  const lo = Number(low);
  const hi = Number(high);
  const pct = cur != null ? Math.max(0, Math.min(100, ((cur - lo) / (hi - lo)) * 100)) : null;
  const flashColor = flash === 'up' ? '#10b981' : flash === 'down' ? '#ef4444' : null;
  return (
    <div className="py-1.5" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</span>
        {cur != null && (
          <span
            className="text-[10px] font-mono font-semibold transition-colors duration-500"
            style={flashColor ? { color: flashColor } : undefined}
            data-testid={`${testId}-current`}
          >
            ${cur.toFixed(2)}
          </span>
        )}
      </div>
      <div className="relative h-1.5 rounded-full bg-gradient-to-r from-red-400/70 via-amber-300/70 to-emerald-400/70">
        {pct != null && (
          <span
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-foreground border-2 border-card shadow transition-[left,background-color,box-shadow] duration-700 ease-out"
            style={{
              left: `${pct}%`,
              ...(flashColor ? { backgroundColor: flashColor, boxShadow: `0 0 0 5px ${flashColor}40` } : {}),
            }}
            data-testid={`${testId}-dot`}
          />
        )}
      </div>
      <div className="flex items-center justify-between mt-0.5">
        <span className="text-[10px] font-mono text-muted-foreground">${lo.toFixed(2)}</span>
        <span className="text-[10px] font-mono text-muted-foreground">${hi.toFixed(2)}</span>
      </div>
    </div>
  );
}

// Compact label/value row for the chart side panel.
// Round P108aq — Beta volatility band mapping. The volatility dot
// AND the "- Low Risk / Neutral / Elevated / High Risk" label right
// after "Beta" both use these hex colours. Exact hex per user spec:
//   • Low Risk       (<0.80)      light #2563EB  dark #60A5FA
//   • Neutral        (0.80-1.20)  light #6B7280  dark #9CA3AF
//   • Elevated       (1.21-1.50)  light #D97706  dark #F59E0B
//   • High Risk      (>1.50)      light #DC2626  dark #EF4444
const BETA_BANDS = {
  low:      { label: 'Low Risk',  light: '#2563EB', dark: '#60A5FA' },
  neutral:  { label: 'Neutral',   light: '#6B7280', dark: '#9CA3AF' },
  elevated: { label: 'Elevated',  light: '#D97706', dark: '#F59E0B' },
  high:     { label: 'High Risk', light: '#DC2626', dark: '#EF4444' },
};

function classifyBetaBand(beta) {
  if (beta == null || !Number.isFinite(Number(beta))) return null;
  const b = Number(beta);
  if (b < 0.80) return 'low';
  if (b <= 1.20) return 'neutral';
  if (b <= 1.50) return 'elevated';
  return 'high';
}

/**
 * Renders the volatility dot + "- Low Risk / Neutral / Elevated /
 * High Risk" chunk that follows the word "Beta" in the side panel.
 * Uses two colour swatches (light/dark) via CSS custom properties
 * so it swaps instantly when the user flips the theme. Returns
 * `null` when Beta is missing/NaN so the row degrades gracefully.
 */
function BetaVolatilityBand({ beta }) {
  const band = classifyBetaBand(beta);
  if (!band) return null;
  const { label, light, dark } = BETA_BANDS[band];
  // We can't use `.dark:text-[#...]` for arbitrary hex if the JIT
  // hasn't seen it; safer to use inline style + a CSS var pair we
  // set on the parent span. Two nested spans with `hidden dark:inline`
  // vs `inline dark:hidden` also works and is what the codebase
  // already uses (see RiskFlags*).
  return (
    <span
      className="inline-flex items-center gap-1"
      data-testid={`side-beta-band-${band}`}
    >
      <span aria-hidden="true">-</span>
      <span
        className="inline dark:hidden font-semibold"
        style={{ color: light }}
      >
        {label}
      </span>
      <span
        className="hidden dark:inline font-semibold"
        style={{ color: dark }}
      >
        {label}
      </span>
    </span>
  );
}

/**
 * Coloured 8px dot used just before the Beta value in the side panel
 * so the reader can spot a high-vol name at a glance without reading
 * the number. Theme-aware via the same light/dark swatches.
 */
function BetaVolatilityDot({ beta }) {
  const band = classifyBetaBand(beta);
  if (!band) return null;
  const { light, dark, label } = BETA_BANDS[band];
  return (
    <>
      <span
        className="inline-block dark:hidden w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: light }}
        title={`Beta volatility: ${label}`}
        data-testid={`side-beta-dot-${band}`}
        aria-label={`Beta volatility: ${label}`}
      />
      <span
        className="hidden dark:inline-block w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: dark }}
        title={`Beta volatility: ${label}`}
        aria-hidden="true"
      />
    </>
  );
}

function SideRow({ label, value, testId, emphasis, labelSuffix, valuePrefix }) {
  if (value == null || value === '' || value === '--') return null;
  // Round P108aq (Feb 2026) — `emphasis` bumps a row to 12px and
  // theme-aware brand color. 'blue' matches the active 1y price-chart
  // period button (Tailwind primary → blue-600 / blue-500). 'purple'
  // matches the RSI14 overlay (violet-500 in both themes). Applied
  // to the "always-render" hero metrics — Market Cap / Beta / Volume
  // (blue) and Fund AUM / NAV (purple) — so admins & readers spot
  // them fast in the sidebar.
  const emphasisText =
    emphasis === 'blue'
      ? 'text-[12px] text-blue-600 dark:text-blue-500'
      : emphasis === 'purple'
        ? 'text-[12px] text-violet-500 dark:text-violet-500'
        : 'text-[11px]';
  const emphasisLabel =
    emphasis === 'blue'
      ? 'text-[12px] font-semibold text-blue-600 dark:text-blue-500'
      : emphasis === 'purple'
        ? 'text-[12px] font-semibold text-violet-500 dark:text-violet-500'
        : 'text-[10px] font-medium text-muted-foreground';
  return (
    <div className="flex items-center justify-between py-[3px] border-b border-border/20 last:border-0" data-testid={testId}>
      <span className={emphasisLabel}>
        {label}
        {labelSuffix}
      </span>
      <span className={`font-mono font-semibold flex items-center gap-1.5 ${emphasisText}`}>
        {valuePrefix}
        <span>{value}</span>
      </span>
    </div>
  );
}

function SectionHeader({ title }) {
  // Fuchsia matches the Compare icon color — chosen by the user for
  // theme-agnostic visibility (no greens/reds/yellows/blues etc).
  return <h4 className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400 mt-4 mb-1.5">{title}</h4>;
}

function RecBadge({ rec }) {
  if (!rec) return <span className="text-xs font-mono">--</span>;
  const colors = {
    strong_buy: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    buy: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    hold: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    sell: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
    strong_sell: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
  const label = rec.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${colors[rec] || 'bg-muted text-muted-foreground'}`}>{label}</span>;
}

function SurpriseBadge({ pct }) {
  if (pct == null) return <span className="text-xs font-mono text-muted-foreground">--</span>;
  const isPositive = pct >= 0;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold font-mono ${
      isPositive ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
    }`}>
      {isPositive ? '+' : ''}{pct.toFixed(2)}%
    </span>
  );
}

function formatQtr(dateStr) {
  if (!dateStr) return '--';
  const d = new Date(dateStr + 'T00:00:00');
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${q} '${String(d.getFullYear()).slice(2)}`;
}

function RsiPanel({ prices, rsiDateMap, height = 70, animKey = 0 }) {
  const rsiWrapRef = useRef(null);
  const measuredWidth = useContainerWidth(rsiWrapRef);
  // Small companion panel rendered BELOW the price chart when RSI14
  // is toggled on. Same X-axis as the price chart, value range 0-100,
  // overbought (70) and oversold (30) reference lines.
  const pathRef = useRef(null);
  const data = useMemo(() => {
    if (!prices || prices.length < 2 || !rsiDateMap) return null;
    const width = measuredWidth;
    const padLeft = 60, padRight = 10, padTop = 6, padBottom = 14;
    const chartW = width - padLeft - padRight;
    const chartH = height - padTop - padBottom;
    const minV = 0;
    const maxV = 100;
    const range = maxV - minV;
    const segs = [];
    let cmd = 'M';
    prices.forEach((p, i) => {
      const v = rsiDateMap[p.date];
      if (v == null || !Number.isFinite(v)) { cmd = 'M'; return; }
      const x = padLeft + (i / (prices.length - 1)) * chartW;
      const y = padTop + chartH - ((v - minV) / range) * chartH;
      segs.push(`${cmd} ${x} ${y}`);
      cmd = 'L';
    });
    const yLine = (val) => padTop + chartH - ((val - minV) / range) * chartH;
    return {
      d: segs.join(' '),
      width, height, padLeft, padRight, chartW, chartH, padTop,
      y30: yLine(30), y70: yLine(70), y50: yLine(50),
    };
  }, [prices, rsiDateMap, height, measuredWidth]);

  // Round P130 (Feb 2026) — Match the price chart's left-to-right
  // draw-in when a fresh fetch resolves. Slightly shorter (~1.2 s)
  // so it lands just after the price line finishes its 1.8 s draw.
  useEffect(() => {
    const el = pathRef.current;
    if (!el) return undefined;
    try {
      const len = el.getTotalLength();
      if (!Number.isFinite(len) || len <= 0) return undefined;
      el.style.transition = 'none';
      el.style.strokeDasharray = `${len}`;
      el.style.strokeDashoffset = `${len}`;
      requestAnimationFrame(() => {
        el.style.transition = 'stroke-dashoffset 1.2s ease-out 0.4s';
        el.style.strokeDashoffset = '0';
      });
    } catch (_e) { /* getTotalLength unsupported in test envs */ }
    return undefined;
  }, [animKey, prices, rsiDateMap, measuredWidth]);

  if (!data) return null;

  return (
    <div className="mt-2" data-testid="rsi-panel" ref={rsiWrapRef}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold text-muted-foreground font-semibold uppercase tracking-wide">RSI 14</span>
        <span className="text-[10px] font-bold text-muted-foreground">Overbought · Oversold</span>
      </div>
      <svg viewBox={`0 0 ${data.width} ${data.height}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        <line x1={data.padLeft} y1={data.y70} x2={data.padLeft + data.chartW} y2={data.y70} className="stroke-rose-400/60" strokeWidth="0.8" strokeDasharray="3,3" />
        <line x1={data.padLeft} y1={data.y30} x2={data.padLeft + data.chartW} y2={data.y30} className="stroke-emerald-400/60" strokeWidth="0.8" strokeDasharray="3,3" />
        <line x1={data.padLeft} y1={data.y50} x2={data.padLeft + data.chartW} y2={data.y50} className="stroke-border/40" strokeWidth="0.5" strokeDasharray="2,4" />
        <text x={data.padLeft - 5} y={data.y70 + 3} textAnchor="end" className="fill-rose-500" fontSize="9" fontFamily="IBM Plex Mono, monospace">70</text>
        <text x={data.padLeft - 5} y={data.y30 + 3} textAnchor="end" className="fill-emerald-500" fontSize="9" fontFamily="IBM Plex Mono, monospace">30</text>
        <text x={data.padLeft - 5} y={data.y50 + 3} textAnchor="end" className="fill-muted-foreground" fontSize="9" fontFamily="IBM Plex Mono, monospace">50</text>
        <path ref={pathRef} d={data.d} fill="none" stroke="#8b5cf6" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </div>
  );
}

const INDICATOR_DEFS = [
  { key: 'sma20',  kind: 'sma', window: 20,  label: 'SMA20',  color: '#22d3ee', dashed: false },
  { key: 'sma50',  kind: 'sma', window: 50,  label: 'SMA50',  color: '#f59e0b', dashed: false },
  { key: 'sma200', kind: 'sma', window: 200, label: 'SMA200', color: '#ef4444', dashed: true  },
  { key: 'rsi14',  kind: 'rsi', window: 14,  label: 'RSI14',  color: '#8b5cf6', dashed: false },
];

// Round 95p (Jun 2026) — Technicals card lives directly under the
// price chart now. It only shows values for currently-active overlay
// toggles, and renders nothing when every overlay is off.
//
// • For SMAs we surface the latest indicator value as a dollar price
//   ($168.42) — that's what users compare against the live price.
// • For RSI we surface the latest 0-100 reading and tag overbought
//   (≥70) / oversold (≤30) regions inline so the user doesn't have
//   to remember the thresholds.
function TechnicalsOverlayCard({ activeIndicators, indicatorData }) {
  const activeDefs = INDICATOR_DEFS.filter((d) => activeIndicators.has(d.key));
  if (activeDefs.length === 0) return null;

  const latestForKey = (key) => {
    const map = indicatorData[key];
    if (!map) return null;
    // dateMap keys are YYYY-MM-DD; lexicographic max == chronological max.
    let bestDate = null;
    for (const d of Object.keys(map)) {
      if (bestDate == null || d > bestDate) bestDate = d;
    }
    if (bestDate == null) return null;
    const v = map[bestDate];
    return Number.isFinite(v) ? v : null;
  };

  const rsiTag = (v) => {
    if (v == null) return null;
    if (v >= 70) return { label: 'Overbought', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' };
    if (v <= 30) return { label: 'Oversold', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' };
    return { label: 'Neutral', cls: 'bg-muted text-muted-foreground' };
  };

  return (
    <div className="rounded-lg border bg-card px-4 py-3" data-testid="technicals-overlay-card">
      <SectionHeader title="Technicals" />
      <div className="flex flex-wrap gap-2 mt-1">
        {activeDefs.map((def) => {
          const isLoading = !indicatorData[def.key];
          const v = latestForKey(def.key);
          const isRsi = def.kind === 'rsi';
          const display = isLoading
            ? '…'
            : v == null
              ? '--'
              : isRsi
                ? v.toFixed(2)
                : `$${v.toFixed(2)}`;
          const tag = isRsi ? rsiTag(v) : null;
          return (
            <div
              key={def.key}
              className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md border"
              style={{
                backgroundColor: `${def.color}1f`, // ~12% opacity
                borderColor: `${def.color}66`,     // ~40% opacity
              }}
              data-testid={`technicals-row-${def.key}`}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: def.color }} />
              <span className="text-xs font-bold uppercase tracking-wide" style={{ color: def.color }}>
                {def.label}
              </span>
              <span className="text-xs font-mono font-semibold text-foreground">
                {display}
              </span>
              {tag && v != null && (
                <span className={`text-[9px] font-bold px-1 py-[1px] rounded-sm ${tag.cls}`}>
                  {tag.label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function StockMetricsTab({ ticker, stock, initialPeriod, livePrice, onOpenStock, intradayEnabled, onNavigateWithOrigin, chartOnly = false, showValuation = true }) {
  const [period, setPeriod] = useState(initialPeriod || '1y');
  const [data, setData] = useState(null);
  // Round 97a (Feb 2026) — When a ticker only has ≤5 years of history
  // on the upstream provider, the "max" range returns the exact same
  // chart as "5y" (e.g. WEN's max snapshot only goes back to Jun 2021
  // even though Wendy's has traded under WEN since 1980). Showing
  // both is misleading — users assume max means "full history". We
  // probe the max endpoint once on mount and set this flag when max
  // returns a span ≤ ~5.5 years, which hides the max button.
  const [maxSameAs5y, setMaxSameAs5y] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Round P130 (Feb 2026) — Draw-in animation key. Bumped whenever a
  // FRESH price-history fetch resolves; the polyline uses this in a
  // stroke-dasharray transition so the chart appears to redraw left
  // to right (~2 s). Cache-hit renders do NOT bump this key so
  // re-visited timelines pop in instantly.
  const [chartAnimKey, setChartAnimKey] = useState(0);
  // Round 85 (Feb 2026) — Tech indicator overlay state. Each entry in
  // `activeIndicators` is one of {sma20, sma50, sma200, rsi14}. SMAs
  // render directly on the price chart; RSI renders as a companion
  // panel below. `indicatorData[key]` is the cached date→value map.
  // Round 95p (Jun 2026) — SMA50 + RSI14 are engaged by default, and
  // the Technicals card now lives directly under the chart, mirroring
  // whichever overlays are active.
  const DEFAULT_OVERLAYS = ['sma50', 'rsi14'];
  const [activeIndicators, setActiveIndicators] = useState(new Set(DEFAULT_OVERLAYS));
  const [indicatorData, setIndicatorData] = useState({});

  // Round P130b (Feb 2026) — Prefetch-on-hover. When the user hovers
  // a timeline button for 300 ms (past accidental drift), quietly
  // fetch that period's price-history + SMA/RSI overlays into the
  // module caches. If they then click, the useEffect finds a cache
  // hit and renders instantly — no spinner, no draw animation, no
  // network wait. NOTE: declared AFTER `activeIndicators` so its
  // useCallback closure doesn't hit a TDZ error.
  const hoverTimersRef = useRef({});
  const inflightRef = useRef(new Set());
  const prefetchPeriod = useCallback((p) => {
    if (!ticker || !p) return;
    const key = `${ticker}:${p}`;
    if (_priceHistoryCache.has(key) || inflightRef.current.has(key)) return;
    inflightRef.current.add(key);
    axios
      .get(`${API}/stocks/${ticker}/price-history`, {
        params: { period: p }, withCredentials: true,
      })
      .then((res) => {
        _lruSet(_priceHistoryCache, key, { data: res.data, cachedAt: Date.now() }, PRICE_CACHE_MAX);
      })
      .catch(() => { /* silent — a real click will retry */ })
      .finally(() => { inflightRef.current.delete(key); });
    // Also warm the currently-active indicator overlays for that
    // period so SMA/RSI don't lag behind the price line on the first
    // real click.
    const limitByPeriod = { '1mo': 30, '3mo': 90, '6mo': 180, '1y': 300, '5y': 1300, max: 1300 };
    const limit = limitByPeriod[p] || 300;
    activeIndicators.forEach((k) => {
      const mKey = `${ticker}:${k}:${p}`;
      if (_indicatorSeriesCache.has(mKey) || inflightRef.current.has(mKey)) return;
      const def = INDICATOR_DEFS.find((d) => d.key === k);
      if (!def) return;
      inflightRef.current.add(mKey);
      axios
        .get(`${API}/stocks/${ticker}/indicator-series`, {
          params: { kind: def.kind, window: def.window, limit },
          withCredentials: true,
        })
        .then((r) => {
          const series = r.data?.series || [];
          const dateMap = {};
          for (const pt of series) {
            const d = new Date(pt.timestamp);
            dateMap[d.toISOString().slice(0, 10)] = pt.value;
          }
          _lruSet(_indicatorSeriesCache, mKey, dateMap, INDICATOR_CACHE_MAX);
        })
        .catch(() => { /* silent */ })
        .finally(() => { inflightRef.current.delete(mKey); });
    });
  }, [ticker, activeIndicators]);

  const onPeriodHoverStart = useCallback((p) => {
    if (hoverTimersRef.current[p]) return;
    hoverTimersRef.current[p] = setTimeout(() => {
      hoverTimersRef.current[p] = null;
      prefetchPeriod(p);
    }, 300);
  }, [prefetchPeriod]);
  const onPeriodHoverEnd = useCallback((p) => {
    const t = hoverTimersRef.current[p];
    if (t) {
      clearTimeout(t);
      hoverTimersRef.current[p] = null;
    }
  }, []);
  // Clean up any pending hover timers on ticker change / unmount so a
  // dangling timeout can't fetch for an already-closed modal.
  useEffect(() => {
    return () => {
      Object.values(hoverTimersRef.current).forEach((t) => t && clearTimeout(t));
      hoverTimersRef.current = {};
    };
  }, [ticker]);
  // Round 102 (Feb 2026) — Period-scoped view of `indicatorData`.
  // Storage is keyed by `${key}:${period}` so 1y and 5y fetches for
  // the same indicator don't collide; consumers below still read by
  // `key` (e.g. `data.sma50`) via this memoized adapter.
  const periodIndicatorData = useMemo(() => {
    const out = {};
    for (const def of INDICATOR_DEFS) {
      const scoped = indicatorData[`${def.key}:${period}`];
      if (scoped) out[def.key] = scoped;
    }
    return out;
  }, [indicatorData, period]);

  // Round 102b (Feb 2026) — Golden/Death cross detector. Walks every
  // date where BOTH SMA50 and SMA200 have values in chronological
  // order; a sign change in (sma50 - sma200) between two consecutive
  // days is a crossover event. Only renders when both overlays are
  // active — that's the user's explicit intent to see the trend
  // regime, not a surprise annotation. Golden = SMA50 crossed ABOVE
  // SMA200 (bullish regime start). Death = SMA50 crossed BELOW SMA200
  // (bearish regime start). Empty array when either series is missing.
  const crossovers = useMemo(() => {
    if (!activeIndicators.has('sma50') || !activeIndicators.has('sma200')) return [];
    const sma50Map = periodIndicatorData.sma50;
    const sma200Map = periodIndicatorData.sma200;
    if (!sma50Map || !sma200Map) return [];
    const dates = Object.keys(sma50Map).filter((d) => sma200Map[d] != null).sort();
    const out = [];
    let prevSign = null;
    for (const d of dates) {
      const diff = sma50Map[d] - sma200Map[d];
      const sign = diff > 0 ? 1 : diff < 0 ? -1 : 0;
      if (sign === 0) continue;
      if (prevSign != null && sign !== prevSign) {
        out.push({ date: d, kind: sign > 0 ? 'golden' : 'death' });
      }
      prevSign = sign;
    }
    return out;
  }, [activeIndicators, periodIndicatorData]);

  // Round 104b (Jun 2026) — history-truncation note. Recent IPOs /
  // spin-offs (e.g. SNDK, listed Feb 2025) don't have 200 trading
  // days of history, so SMA200 can't print until the window fills —
  // the line starts months into the chart. Users read that as a data
  // bug ("why is SMA200 only 6 months?"), so when an active SMA
  // series starts >30 days after the chart's first bar we say why.
  const truncatedOverlays = useMemo(() => {
    const chartFirst = data?.prices?.[0]?.date;
    if (!chartFirst) return [];
    const out = [];
    for (const def of INDICATOR_DEFS) {
      if (def.kind !== 'sma' || !activeIndicators.has(def.key)) continue;
      const map = periodIndicatorData[def.key];
      if (!map) continue;
      let firstDate = null;
      for (const d of Object.keys(map)) {
        if (firstDate == null || d < firstDate) firstDate = d;
      }
      if (!firstDate) continue;
      const gapDays = (new Date(firstDate) - new Date(chartFirst)) / 86400000;
      if (gapDays > 30) {
        const label = new Date(`${firstDate}T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        out.push({ key: def.key, name: def.label, begins: label });
      }
    }
    return out;
  }, [data, activeIndicators, periodIndicatorData]);
  // Local override that accumulates fields arriving from background
  // Massive enrichment AFTER the modal has already opened. Parent
  // pages other than Dashboard (Screener, Challenges, SectorView,
  // Rebalancer) don't poll `/api/stocks/{ticker}` for late updates, so
  // without this override, CEFs like UTG/KYN/PDI show only 2–3 tables
  // until the user closes and reopens the modal. We own the poll here
  // so the tab is self-healing no matter who opened it.
  const [stockOverride, setStockOverride] = useState(null);

  useEffect(() => {
    if (!ticker) return;
    const cacheKey = `${ticker}:${period}`;
    const cached = _priceHistoryCache.get(cacheKey);
    if (cached) {
      // Round P130 — instant restore from in-modal cache. Skip
      // loading state entirely so the chart doesn't blink and the
      // cards above/below (REIT Cash Lens etc.) stay perfectly still.
      setData(cached.data);
      setLoading(false);
      setError(null);
      setChartAnimKey((k) => k); // do NOT restart draw animation on cache hit
      return;
    }
    setLoading(true);
    setError(null);
    axios.get(`${API}/stocks/${ticker}/price-history`, { params: { period }, withCredentials: true })
      .then(res => {
        setData(res.data);
        _lruSet(_priceHistoryCache, cacheKey, { data: res.data, cachedAt: Date.now() }, PRICE_CACHE_MAX);
        // Bump the animation key so the polyline redraws left-to-right.
        setChartAnimKey((k) => k + 1);
      })
      .catch(() => setError('Could not load price history'))
      .finally(() => setLoading(false));
  }, [ticker, period]);

  // Reset the override whenever we switch to a new stock — otherwise
  // the previous ticker's late-arriving fields would bleed into the
  // next one (only matters during the first ~30s after each open).
  useEffect(() => {
    setStockOverride(null);
    setActiveIndicators(new Set(DEFAULT_OVERLAYS));
    setIndicatorData({});
    setMaxSameAs5y(false);
  }, [ticker]);

  // Round 97a — Probe max once per ticker. If max history is ≤5.5y
  // of data, hide the max button (it's identical to 5y for this
  // ticker). Cheap because the backend caches price-history per
  // ticker × period.
  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/stocks/${ticker}/price-history`, {
        params: { period: 'max' }, withCredentials: true,
      })
      .then((r) => {
        if (cancelled) return;
        const hist = r.data?.prices || [];
        if (!hist.length) return;
        const earliest = new Date(hist[0].date);
        const ageMs = Date.now() - earliest.getTime();
        const ageYears = ageMs / (365.25 * 24 * 3600 * 1000);
        // 5.5y threshold gives a little buffer for tickers that have
        // ~5y of data plus a handful of extra weeks.
        setMaxSameAs5y(ageYears <= 5.5);
      })
      .catch(() => { /* swallow — leave max button visible if probe fails */ });
    return () => { cancelled = true; };
  }, [ticker]);

  // Round 97a — Bounce away from max if we just determined it's
  // identical to 5y (user could have selected max before the probe
  // resolved).
  useEffect(() => {
    if (maxSameAs5y && period === 'max') setPeriod('5y');
  }, [maxSameAs5y, period]);

  // Fetch any newly-toggled indicator series. Each series is cached
  // in-component so toggling off/on doesn't re-fire the request.
  //
  // Round 102 fix (Feb 2026) — the cache used to be keyed only by
  // `key` (sma20 / sma50 / …). That collapsed the 1y and 5y fetches
  // into the same slot, so an SMA50 series first pulled at 1y
  // (limit=300) would stay in state when the user switched to 5y
  // (limit=1300) — chart drew only ~1yr of overlay on the 5y view.
  // Now the in-component cache key includes the period, and the
  // downstream lookups all read `indicatorData[`${key}:${period}`]`.
  // EPS Beat/Miss — one Finnhub pull per modal open (server caches per ticker).
  const [epsFinnhub, setEpsFinnhub] = useState(null);
  useEffect(() => {
    if (!ticker || chartOnly) return undefined;
    let cancelled = false;
    setEpsFinnhub(null);
    axios.get(`${API}/stocks/${ticker}/eps-surprises`, { withCredentials: true, timeout: 15000 })
      .then(({ data }) => { if (!cancelled && data?.source === 'finnhub' && data.eps_history?.length) setEpsFinnhub(data.eps_history); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker, chartOnly]);


  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    const limitByPeriod = { '1mo': 30, '3mo': 90, '6mo': 180, '1y': 300, '5y': 1300, max: 1300 };
    const limit = limitByPeriod[period] || 300;
    activeIndicators.forEach((key) => {
      const cacheKey = `${key}:${period}`;
      if (indicatorData[cacheKey]) return;
      // Round P130 — consult module-scoped cache before hitting the
      // network. Same modal session, same (ticker,indicator,period)
      // = instant hydrate, no fetch.
      const moduleCacheKey = `${ticker}:${key}:${period}`;
      const cached = _indicatorSeriesCache.get(moduleCacheKey);
      if (cached) {
        setIndicatorData((prev) => ({ ...prev, [cacheKey]: cached }));
        return;
      }
      const def = INDICATOR_DEFS.find((d) => d.key === key);
      if (!def) return;
      axios
        .get(`${API}/stocks/${ticker}/indicator-series`, {
          params: { kind: def.kind, window: def.window, limit },
          withCredentials: true,
        })
        .then((r) => {
          if (cancelled) return;
          const series = r.data?.series || [];
          const dateMap = {};
          for (const pt of series) {
            // Massive timestamps are ms-epoch UTC. Convert to YYYY-MM-DD
            // (UTC slice) to align with the price chart's `date` strings.
            const d = new Date(pt.timestamp);
            const ymd = d.toISOString().slice(0, 10);
            dateMap[ymd] = pt.value;
          }
          _lruSet(_indicatorSeriesCache, moduleCacheKey, dateMap, INDICATOR_CACHE_MAX);
          setIndicatorData((prev) => ({ ...prev, [cacheKey]: dateMap }));
        })
        .catch(() => { /* silent — overlay just won't draw */ });
    });
    return () => { cancelled = true; };
  }, [ticker, activeIndicators, period, indicatorData]);

  // Self-healing background poll: every 2 s for up to 30 s, re-fetch
  // `/api/stocks/{ticker}` and merge in any previously-missing fields.
  // Stops as soon as the known "slow-arrival" fields (perf_1m, ROE,
  // profit_margins etc.) are all populated — usually within the first
  // 1–2 polls. Cheap (~1 round-trip per 2 s, 15 max).
  useEffect(() => {
    if (!ticker || !stock) return;
    // Fields that are known to arrive AFTER the initial /api/stocks
    // response for CEFs / ETFs / stocks needing Massive enrichment.
    // If ANY of these is missing, we poll — once they're all present,
    // the tab has everything the user expects.
    const merged = { ...(stock || {}), ...(stockOverride || {}) };
    const stillMissing = (
      !isFiniteValue(merged.perf_1m) ||
      !isFiniteValue(merged.perf_6m) ||
      !(isFiniteValue(merged.return_on_equity) || isFiniteValue(merged.quality_return_on_equity)) ||
      !(isFiniteValue(merged.profit_margins) || isFiniteValue(merged.quality_net_margin)) ||
      !isFiniteValue(merged.trailing_pe) ||
      !merged.profile_data ||
      // Round 95n — beta + 52-week range now arrive via the background
      // FMP fill a second or two after the modal opens. Poll for them
      // so the side-panel range bars appear without a reopen.
      !isFiniteValue(merged.beta) ||
      !(isFiniteValue(merged.fifty_two_week_low) || isFiniteValue(merged['52_week_low']))
    );
    if (!stillMissing) return;
    let cancelled = false;
    let tries = 0;
    const maxTries = 15;
    const tick = async () => {
      if (cancelled || tries >= maxTries) return;
      tries += 1;
      try {
        const { data: fresh } = await axios.get(`${API}/stocks/${ticker}`, { withCredentials: true });
        if (cancelled) return;
        setStockOverride(prev => {
          const base = { ...(stock || {}), ...(prev || {}) };
          let changed = false;
          const next = { ...(prev || {}) };
          for (const k of Object.keys(fresh)) {
            const v = fresh[k];
            const incomingReal = v !== null && v !== undefined && v !== '';
            const existingReal = base[k] !== null && base[k] !== undefined && base[k] !== '' && base[k] !== 0;
            if (incomingReal && !existingReal) {
              next[k] = v;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      } catch {}
    };
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
    // Intentionally depend on the RESOLVED merged stock fields so
    // `stillMissing` re-evaluates after each successful merge (and
    // exits the poll loop as soon as we're complete).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker, stock?.perf_1m, stock?.perf_6m, stock?.return_on_equity, stock?.profit_margins, stock?.trailing_pe, stock?.profile_data, stockOverride?.perf_1m, stockOverride?.perf_6m, stockOverride?.return_on_equity, stockOverride?.profit_margins, stockOverride?.trailing_pe, stockOverride?.profile_data]);

  // Round P130 (Feb 2026) — DON'T early-return a full-tab spinner
  // here. Doing so would unmount the ReitCashLensPanel + every card
  // below/above the chart on every timeline switch — causing the
  // Cash Lens to refetch (heavy REIT/MLP endpoint), the whole page
  // to collapse, then re-expand as data resolves. Instead we let
  // the chart section itself render a placeholder while `loading`,
  // keeping the rest of the tab perfectly still.
  //
  // Round 92o++++++++i (Feb 2026) — Soft-fail the Yahoo price-history call.
  // 95% of what renders below (valuation, profitability, growth, earnings,
  // analyst targets, REIT cash lens, holdings, fund profile, Massive
  // technicals) is sourced from `s` (the Massive-enriched stock prop), not
  // from `data`. When Yahoo throttles a fresh deployment pod, the user pays
  // for Massive data they should still see. Render the chart section as a
  // placeholder when `data` is missing and let everything else render
  // normally off `s`.
  const chartUnavailable = !!error || !data;

  const m = data?.metrics || {};
  // Merge the live prop with any late-arriving fields from the
  // background enrichment poll (see stockOverride useEffect above).
  const sRaw = { ...(stock || {}), ...(stockOverride || {}) };
  // Field fallbacks: the Massive financials path (Feb 2026 — replaces Finimpulse)
  // populates new `quality_*` fields (revenue_growth, eps_growth,
  // return_on_equity, return_on_invested_capital, free_cash_flow_margin,
  // net_margin, revenue_stability, debt_to_equity). For tickers
  // freshly enriched by the batch pull but not yet by yfinance, the
  // legacy fields are missing — so we coalesce here in one place
  // instead of touching every renderer below.
  const s = {
    ...sRaw,
    return_on_equity:            sRaw.return_on_equity            ?? sRaw.quality_return_on_equity,
    return_on_invested_capital:  sRaw.return_on_invested_capital  ?? sRaw.quality_return_on_invested_capital,
    free_cash_flow_margin:       sRaw.free_cash_flow_margin       ?? sRaw.quality_free_cash_flow_margin,
    net_margin:                  sRaw.net_margin                  ?? sRaw.quality_net_margin,
    revenue_growth:              sRaw.revenue_growth              ?? sRaw.quality_revenue_growth,
    eps_growth:                  sRaw.eps_growth                  ?? sRaw.quality_eps_growth,
    revenue_stability:           sRaw.revenue_stability           ?? sRaw.quality_revenue_stability,
    debt_to_equity:              sRaw.debt_to_equity              ?? sRaw.quality_debt_to_equity,
    profit_margins:              sRaw.profit_margins              ?? sRaw.quality_net_margin,
  };
  // Day-change badge — prefer Yahoo's value when present, fall back to
  // Massive's `day_change_pct` (written by the daily grouped snapshot)
  // so the chart header still shows today's move when Yahoo throttles.
  const changePctValue = data?.change_pct ?? s.day_change_pct ?? null;
  const isPositive = (changePctValue || 0) >= 0;

  // Earnings data (inline in right column, not separate section)
  const ed = s.earnings_data || {};
  const eps = epsFinnhub ?? (ed.eps_history || []);
  const earningsGrowth = ed.growth || [];
  const earningsRevisions = ed.eps_revisions || [];
  const earningsTrend = ed.eps_trend || [];
  const hasEarnings = eps.length > 0 || earningsGrowth.length > 0;

  // Holdings data (ETFs/funds)
  const hd = s.holdings_data || {};
  const holdingsAlloc = hd.asset_allocation || {};
  const holdingsSectors = hd.sector_weights || {};
  const holdingsValuation = hd.holdings_valuation || {};
  const hasHoldings = Object.keys(holdingsAlloc).length > 0 || Object.keys(holdingsSectors).length > 0;

  // Fund Profile data (ETFs/funds) — values come from two sources:
  //   1. Top-level fields filled by the Yahoo Metric Warmer (expense_ratio,
  //      net_assets, fund_category, fund_family, nav_price).
  //   2. Massive profile_data (turnover, fund_size_style, fund_value_style,
  //      morningstar_rating) — kept as a fallback so older ETFs that were
  //      enriched before the warmer still render whatever data we DO have.
  const pd = s.profile_data || {};
  const fundProfile = {
    expense_ratio:     s.expense_ratio    ?? pd.expense_ratio    ?? null,
    expense_ratio_source: s.expense_ratio_source ?? null,
    expense_ratio_as_of:  s.expense_ratio_as_of  ?? null,
    expense_ratio_gross:  s.expense_ratio_gross  ?? null,
    net_assets:        s.net_assets       ?? pd.net_assets       ?? null,
    // Oct 2026 — provenance: SEC N-PORT (monthly filing, ~60-day lag) vs Yahoo.
    net_assets_source: s.net_assets_source ?? null,
    net_assets_as_of:  s.net_assets_as_of  ?? null,
    category:          s.fund_category    ?? null,
    fund_family:       s.fund_family      ?? null,
    nav_price:         s.nav_price        ?? null,
    turnover:          pd.turnover        ?? null,
    fund_size_style:   pd.fund_size_style ?? null,
    fund_value_style:  pd.fund_value_style?? null,
    morningstar_rating: pd.morningstar_rating ?? null,
  };
  const isFund = (s.asset_type || '').toUpperCase() === 'ETF'
    || (pd.quote_type || '').toUpperCase() === 'ETF'
    || hasHoldings;
  const hasFundProfile = isFund && Object.values(fundProfile).some((v) => v !== null && v !== '');

  // Round 95n — chart side panel data: 52-week + day range sliders fed
  // the LIVE streamed price (SSE) so the dot moves in real time, plus
  // a compact key-metric list on desktop. Field coalescing: the cache
  // carries 52-week bounds under two historical spellings.
  const rangeCurrent = livePrice ?? m.current_price ?? s.price;
  const low52 = s.fifty_two_week_low ?? s['52_week_low'];
  const high52 = s.fifty_two_week_high ?? s['52_week_high'];
  // Today's Massive snapshot (s.day_*) wins over the price-history
  // metrics (m.day_*) — the latter reflects the LAST DAILY BAR, which
  // is yesterday's session after close, leaving the live dot clamped
  // outside the band.
  const dayLow = s.day_low ?? m.day_low;
  const dayHigh = s.day_high ?? m.day_high;
  const hasRanges = (isFiniteValue(low52) && isFiniteValue(high52)) || (isFiniteValue(dayLow) && isFiniteValue(dayHigh));

  // Sep 2026 — one renderer for the chart side stats. Desktop shows them
  // in the right-hand column; phones (< lg) get the SAME rows stacked
  // under the chart instead of a trimmed subset.
  const renderSidePanelRows = (sfx = '') => (
    <>
            {fundProfile.expense_ratio != null && (
              <SideRow label="Expense Ratio" value={`${formatNum(fundProfile.expense_ratio)}%`} testId={`side-expense-ratio${sfx}`} />
            )}
            {s.frequency && <SideRow label="Div Frequency" value={s.frequency} testId={`side-div-frequency${sfx}`} />}
            {isFiniteValue(s.dividend_rate) && Number(s.dividend_rate) > 0 && (
              <SideRow label="Div Rate (TTM)" value={`$${Number(s.dividend_rate).toFixed(2)}`} testId={`side-div-rate${sfx}`} />
            )}
            {isFiniteValue(s.dividend_yield) && Number(s.dividend_yield) > 0 && (
              <SideRow label="Yield (TTM)" value={`${Number(s.dividend_yield).toFixed(2)}%`} testId={`side-yield${sfx}`} />
            )}
            {isFund && fundProfile.net_assets != null ? (
              <SideRow label="Fund AUM" value={formatMoney(fundProfile.net_assets)} testId={`side-aum${sfx}`} emphasis="purple" />
            ) : (s.market_cap != null && (
              <SideRow label="Market Cap" value={formatMoney(s.market_cap)} testId={`side-market-cap${sfx}`} emphasis="blue" />
            ))}
            <div className="mt-1.5 pt-1.5 border-t border-border/40">
              <RangeBar label="52 Week Range" low={low52} high={high52} current={rangeCurrent} testId={`range-52w${sfx}`} />
            </div>
            {isFiniteValue(s.beta) && (
              <SideRow
                label="Beta"
                value={formatNum(s.beta)}
                testId={`side-beta${sfx}`}
                emphasis="blue"
                labelSuffix={<span className="ml-1"><BetaVolatilityBand beta={s.beta} /></span>}
                valuePrefix={<BetaVolatilityDot beta={s.beta} />}
              />
            )}
            <div className="mt-1.5 pt-1.5 border-t border-border/40">
              <RangeBar label="Day Range" low={dayLow} high={dayHigh} current={rangeCurrent} testId={`range-day${sfx}`} />
            </div>
            {(m.volume ?? s.volume) != null && (() => {
              const vol = m.volume ?? s.volume;
              const avg = m.avg_volume ?? s.avg_volume;
              // Round 97a — "20M vs Avg 15M" inline volume display
              // so users can eyeball heavy/light days at a glance
              // without scrolling to the Price & Market card.
              const value = (avg != null && Number(avg) > 0)
                ? `${formatVolume(vol)} vs Avg ${formatVolume(avg)}`
                : formatVolume(vol);
              return <SideRow label="Volume" value={value} testId={`side-volume${sfx}`} emphasis="blue" />;
            })()}
            {fundProfile.nav_price != null && (
              <SideRow label="NAV" value={`$${formatNum(fundProfile.nav_price)}`} testId={`side-nav${sfx}`} emphasis="purple" />
            )}
    </>
  );


  return (
    <div className="space-y-2" data-testid="stock-metrics-tab">
      {/* Quality Score narrative — plain-English summary of why this
          stock got the Quality Score it did. Mirrors the Triangle
          narrative panel on /triangle/:ticker. Renders nothing when
          the stock has no quality data. */}
      {!chartOnly && <QualityNarrativePanel stock={s} />}

      {/* Round 95d (Jun 2026) — Valuation Multiples row.
          4 tiles (Trailing P/E, Forward P/E, P/S, PEG) with "vs Sector
          Median" delta beside each. PEG is Massive-first (computed
          from P/E ÷ EPS-5yr-CAGR) with Yahoo fallback per the
          strict "never overwrite Massive" rule. */}
      {showValuation && <ValuationMultiplesCard stock={s} onOpenStock={onOpenStock} peersClickable={!chartOnly} />}

      {/* REIT/MLP Cash Lens — renders only when stock.is_reit or
          stock.is_mlp is true. Shows FFO (REITs) / DCF (MLPs) per
          share + payout ratio because GAAP EPS is misleading for both
          vehicle types (heavy non-cash depreciation on buildings /
          pipelines). */}
      {!chartOnly && <ReitCashLensPanel ticker={ticker} stock={s} />}

      {/* Price Chart */}
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wider text-fuchsia-500 dark:text-fuchsia-400">
            Price Chart
            {intradayEnabled && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => {
                    // Cross-tab link — StockModal has a global
                    // `stock-modal-switch-tab` listener that also
                    // scrolls the modal to top on mobile so the tab
                    // strip is visible again.
                    window.dispatchEvent(new CustomEvent('stock-modal-switch-tab', { detail: { tab: 'intraday' } }));
                  }}
                  className="inline-flex items-center gap-0.5 text-xs font-semibold normal-case tracking-normal text-fuchsia-500 dark:text-fuchsia-400 hover:underline underline-offset-2"
                  data-testid="price-chart-view-todays-link"
                >
                  <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
                  View Today&apos;s
                </button>
              </>
            )}
          </h3>
          {changePctValue != null && (
            <span className={`text-sm font-bold font-mono ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-testid="chart-change-pct">
              {isPositive ? '+' : ''}{Number(changePctValue).toFixed(2)}%
            </span>
          )}
        </div>
        <div className="flex gap-1 mb-3" data-testid="period-buttons">
          {PERIODS.filter(p => !(p.key === 'max' && maxSameAs5y)).map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              onMouseEnter={() => onPeriodHoverStart(p.key)}
              onMouseLeave={() => onPeriodHoverEnd(p.key)}
              onFocus={() => onPeriodHoverStart(p.key)}
              onBlur={() => onPeriodHoverEnd(p.key)}
              data-testid={`period-btn-${p.key}`}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${period === p.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {/* Round 85 (Feb 2026) — Technical indicator toggles. SMAs
            render as overlay lines on the price chart; RSI14 renders
            its own 0-100 panel below. Lazy-fetched via Massive
            /v1/indicators/{sma,rsi}/{ticker}. */}
        <div className="flex items-center gap-1 mb-3 flex-wrap" data-testid="indicator-toggles">
          <span className="text-[10px] font-bold text-muted-foreground font-semibold uppercase tracking-wide mr-1">Overlays</span>
          {INDICATOR_DEFS.map((def) => {
            const isActive = activeIndicators.has(def.key);
            const isLoading = isActive && !periodIndicatorData[def.key];
            return (
              <button
                key={def.key}
                type="button"
                data-testid={`indicator-toggle-${def.key}`}
                onClick={() => {
                  setActiveIndicators((prev) => {
                    const next = new Set(prev);
                    if (next.has(def.key)) next.delete(def.key);
                    else next.add(def.key);
                    return next;
                  });
                }}
                className={`px-2 py-0.5 text-[10px] font-mono font-semibold rounded-md transition-colors border ${isActive ? 'border-transparent text-white' : 'border-border text-muted-foreground hover:text-foreground'}`}
                style={isActive ? { backgroundColor: def.color } : undefined}
                title={`${def.label} — Massive ${def.kind.toUpperCase()}(${def.window})`}
              >
                {def.label}{isLoading ? '…' : ''}
              </button>
            );
          })}
          {/* Round 102b — Golden/Death cross legend. Only surfaces
              once when both SMA50 + SMA200 are active AND we found
              at least one historical crossover on the current
              period. Keeps the chart uncluttered otherwise. */}
          {crossovers.length > 0 && (
            <span
              className="ml-2 inline-flex items-center gap-2 text-[10px] text-muted-foreground"
              data-testid="crossover-legend"
              title={`${crossovers.length} SMA50/SMA200 crossover${crossovers.length === 1 ? '' : 's'} on the ${period} window.\n▲ Golden Cross = SMA50 crossed ABOVE SMA200 (bullish trend regime).\n▼ Death Cross = SMA50 crossed BELOW SMA200 (bearish trend regime).`}
            >
              <span className="inline-flex items-center gap-0.5"><span className="text-emerald-500">▲</span>Golden</span>
              <span className="inline-flex items-center gap-0.5"><span className="text-rose-500">▼</span>Death</span>
            </span>
          )}
        </div>
        {/* Round 104b — explain SMA lines that start mid-chart on
            tickers without enough trading history (recent IPOs /
            spin-offs). An SMA-200 needs 200 trading days before its
            first point can exist. */}
        {truncatedOverlays.length > 0 && (
          <div className="-mt-2 mb-3 text-[10px] text-amber-600 dark:text-amber-400" data-testid="overlay-truncation-note">
            {truncatedOverlays.map((t) => `${t.name} begins ${t.begins}`).join(' · ')} — limited trading history (an SMA needs that many trading days before its first point)
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 min-w-0">
            {/* Round P130 (Feb 2026) — Fixed-height chart container.
                Height reserved regardless of loading / cache-hit /
                error state so timeline switches never collapse the
                layout below (previously the whole tab jumped when
                the chart card mounted/unmounted).

                • loading (no prior data)   → chart-only spinner card
                • loading (with prior data) → keep old chart visible + subtle overlay
                • ready                     → PriceChart + optional RSI
                • error / no data           → soft-fail message
            */}
            {loading && !data ? (
              <div
                className="flex flex-col items-center justify-center min-h-[260px] rounded-md border border-border/40 bg-background/30"
                data-testid="stock-metrics-chart-loading"
              >
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mb-2" />
                <p className="text-[11px] text-muted-foreground">Drawing chart…</p>
              </div>
            ) : chartUnavailable ? (
              // Round 92o++++++++i — Soft fallback when the price-history
              // call fails. Earlier this hid the WHOLE tab; now we just
              // hide the chart so the metric sections below still render.
              // Round 95l (Jun 2026) — message updated: the chart is
              // Massive-primary now (Yahoo is a last-resort fallback), so
              // blaming "Yahoo rate limits" was misleading. The realistic
              // causes are a brand-new listing or a fresh ticker change
              // whose history hasn't been stitched/indexed yet.
              <div className="flex flex-col items-center justify-center min-h-[260px] text-center px-4" data-testid="stock-metrics-chart-unavailable">
                <TrendingDown className="w-8 h-8 mb-2 text-muted-foreground" />
                <p className="text-sm font-semibold text-muted-foreground">{error || 'Price chart temporarily unavailable'}</p>
                <p className="text-xs font-bold text-muted-foreground mt-1 max-w-md">
                  We couldn&apos;t pull price bars for this ticker right now — this usually means it&apos;s a very new listing or the symbol just changed. The metrics below are sourced from Massive and load independently. Try a different period or check back in a few minutes.
                </p>
              </div>
            ) : (
              <div className={loading ? 'opacity-70 transition-opacity duration-200' : 'transition-opacity duration-200'}>
                <PriceChart
                  prices={data.prices}
                  changePct={data.change_pct}
                  period={period}
                  overlays={INDICATOR_DEFS
                    .filter((def) => def.kind === 'sma' && activeIndicators.has(def.key) && periodIndicatorData[def.key])
                    .map((def) => ({ key: def.key, color: def.color, dashed: def.dashed, dateMap: periodIndicatorData[def.key] }))}
                  crossovers={crossovers}
                  animKey={chartAnimKey}
                />
                {activeIndicators.has('rsi14') && periodIndicatorData.rsi14 && (
                  <RsiPanel prices={data.prices} rsiDateMap={periodIndicatorData.rsi14} animKey={chartAnimKey} />
                )}
              </div>
            )}
            {/* Phone view — Sep 2026: the FULL side-panel stat set
                (Market Cap / AUM, 52w range, Beta, Day range, Volume vs
                Avg, NAV, expense ratio) stacks under the chart. It used
                to render only the range bars + dividend triplet here. */}
            <div className="lg:hidden mt-3 pt-2 border-t border-border/40 space-y-0.5" data-testid="chart-ranges-mobile">
              {renderSidePanelRows('-mobile')}
            </div>
          </div>
          {/* Desktop side panel — Round 97a reorder: dividend stats
              first, then Market Cap, then 52w range bar, then Beta,
              then Day Range bar, then Volume (with Avg comparison
              inline). Old layout had both range bars stacked at the
              top with everything else dumped underneath. */}
          {hasRanges && (
            <div className="hidden lg:block w-[230px] shrink-0 border-l border-border/40 pl-4 space-y-0.5" data-testid="chart-side-panel">
              {renderSidePanelRows()}
            </div>
          )}
        </div>
      </div>

      {/* Round 95p (Jun 2026) — Technicals card sits right under the
          price chart and mirrors the overlay toggles above. Renders
          nothing when every overlay is off. SMA50 + RSI14 default to
          engaged so the card is populated on first open. */}
      <TechnicalsOverlayCard activeIndicators={activeIndicators} indicatorData={periodIndicatorData} />

      {/* Dynamic masonry layout for metric sections — packs side by side, wraps naturally */}
      {!chartOnly && <div className="columns-1 sm:columns-2 gap-3 space-y-3" data-testid="metrics-grid">
          {/* Price & Market — fields gracefully fall back to the
              Massive-sourced `s` (stock_cache) values when the Yahoo
              price-history call is unavailable, so $300/yr Massive
              data still renders. The Massive grouped snapshot writes
              `price`, `day_open`, `day_high`, `day_low`, `volume`,
              `day_change_pct` every morning — we use day_open as the
              prev-close proxy (same proxy massive_snapshot.py already
              uses for its own change% calc). */}
          <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
            <SectionHeader title="Price & Market" />
            <MetricRow
              label="Current Price"
              value={(m.current_price ?? s.price) != null ? `$${Number(m.current_price ?? s.price).toFixed(2)}` : '--'}
              testId="m-price"
              defKey="CURRENT_PRICE"
            />
            {(m.previous_close ?? s.previous_close ?? s.day_open) != null && (
              <MetricRow
                label="Previous Close"
                value={`$${Number(m.previous_close ?? s.previous_close ?? s.day_open).toFixed(2)}`}
                testId="m-prev-close"
                defKey="PREVIOUS_CLOSE"
              />
            )}
            {((m.day_low ?? s.day_low) != null && (m.day_high ?? s.day_high) != null) && (
              <MetricRow
                label="Day Range"
                value={`$${Number(m.day_low ?? s.day_low).toFixed(2)} - $${Number(m.day_high ?? s.day_high).toFixed(2)}`}
                testId="m-day-range"
                defKey="DAY_RANGE"
              />
            )}
            {(s.fifty_two_week_low != null || s.fifty_two_week_high != null) && (
              <MetricRow label="52-Week Range" value={`$${s.fifty_two_week_low?.toFixed(2) ?? '--'} - $${s.fifty_two_week_high?.toFixed(2) ?? '--'}`} testId="m-52w-range" defKey="WEEK_52_RANGE" />
            )}
            {s.market_cap != null && <MetricRow label="Market Cap" value={formatMoney(s.market_cap)} testId="m-market-cap" defKey="MARKET_CAP" />}
            {(m.volume ?? s.volume) != null && (() => {
              const vol = m.volume ?? s.volume;
              const avg = m.avg_volume ?? s.avg_volume;
              const badge = unusualVolumeBadge(vol, avg);
              return (
                <MetricRow
                  label="Volume"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <span>{formatVolume(vol)}</span>
                      {badge && (
                        <span
                          className={`text-[9px] font-bold px-1 py-[1px] rounded-sm whitespace-nowrap ${badge.classes}`}
                          data-testid="stock-metrics-volume-badge"
                          title={
                            badge.severity === 'heavy'
                              ? "Heavy activity — today's volume is well above the 3-month average. Often a news / earnings / dividend-announcement day."
                              : badge.severity === 'above'
                                ? 'Above-average activity vs the 3-month average.'
                                : 'Light activity vs the 3-month average — a quiet day.'
                          }
                        >
                          {badge.label}
                        </span>
                      )}
                    </span>
                  }
                  testId="m-volume"
                  defKey="VOLUME"
                />
              );
            })()}
            {(m.avg_volume ?? s.avg_volume) != null && (
              <MetricRow label="Avg Volume" value={formatVolume(m.avg_volume ?? s.avg_volume)} testId="m-avg-volume" defKey="AVG_VOLUME" />
            )}
          </div>

          {/* Round 95p (Jun 2026) — old Technicals card removed from
              the masonry grid. The new TechnicalsOverlayCard above the
              grid (under the price chart) replaces it, sourced from
              live Massive RSI/SMA series instead of the stock-cache
              50-day/200-day point-in-time averages. Beta still lives
              in the side panel beside the chart. */}

          {/* Valuation. Round 95u (Jun 2026) — reads canonical
              `pe_forward` (legacy `forward_pe` migrated out via
              scripts/migrate_forward_pe_to_pe_forward.py). Optional
              chain to the legacy name kept as a one-cycle safety
              net while any in-flight metric warmer finishes. */}
          {(s.trailing_pe != null || s.pe_forward != null || s.forward_pe != null || s.price_to_book != null || s.book_value != null || s.enterprise_value != null) && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
              <SectionHeader title="Valuation" />
              {s.trailing_pe != null && <MetricRow label="Trailing P/E" value={formatNum(s.trailing_pe)} testId="m-trailing-pe" defKey="TRAILING_PE" />}
              {(s.pe_forward ?? s.forward_pe) != null && <MetricRow label="Forward P/E" value={formatNum(s.pe_forward ?? s.forward_pe)} testId="m-forward-pe" defKey="FORWARD_PE" />}
              {s.price_to_book != null && <MetricRow label="Price/Book" value={formatNum(s.price_to_book)} testId="m-pb" defKey="PRICE_BOOK" />}
              {s.book_value != null && <MetricRow label="Book Value" value={`$${formatNum(s.book_value)}`} testId="m-book-val" defKey="BOOK_VALUE" />}
              {s.enterprise_value != null && <MetricRow label="Enterprise Value" value={formatMoney(s.enterprise_value)} testId="m-ev" defKey="ENTERPRISE_VALUE" />}
            </div>
          )}

          {/* EPS History — Finnhub /stock/earnings on modal open (Sep 2026), cached server-side
              until a week after quarter end; falls back to whatever earnings_data.eps_history holds. */}
          {hasEarnings && eps.length > 0 && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid" data-testid="eps-history-section">
              <SectionHeader title="EPS History (Beat/Miss)" />
              <div className="grid grid-cols-2 gap-2 mt-1">
                {eps.slice(0, 4).map((e, i) => (
                  <div key={i} className="rounded-md border px-2 py-1.5 text-center" data-testid={`eps-quarter-${i}`}>
                    <p className="text-[10px] font-bold text-muted-foreground font-medium">{formatQtr(e.date)}</p>
                    <p className="text-sm font-bold font-mono">${e.actual?.toFixed(2) ?? '--'}</p>
                    <p className="text-[9px] text-muted-foreground">Est: ${e.estimate?.toFixed(2) ?? '--'}</p>
                    <SurpriseBadge pct={e.surprise_pct} />
                  </div>
                ))}
              </div>
              {epsFinnhub && <p className="text-[9px] text-muted-foreground mt-1.5" data-testid="eps-history-source">Quarter-end dates · actual vs consensus · Finnhub</p>}
            </div>
          )}

          {/* Growth vs S&P 500 */}
          {hasEarnings && earningsGrowth.length > 0 && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
              <SectionHeader title="Growth vs S&P 500" />
              <div className="space-y-1">
                {earningsGrowth.slice(0, 4).map((g, i) => {
                  const label = g.date_type === 'quarter' ? formatQtr(g.date) : `FY '${String(new Date(g.date).getFullYear()).slice(2)}`;
                  const stockG = g.growth != null ? (g.growth * 100).toFixed(1) : null;
                  const benchG = g.growth_benchmark != null ? (g.growth_benchmark * 100).toFixed(1) : null;
                  return (
                    <div key={i} className="flex items-center justify-between py-1 border-b border-border/20 last:border-0">
                      <span className="text-xs font-bold text-muted-foreground">{label}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-mono font-semibold ${stockG && parseFloat(stockG) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {stockG ? `${parseFloat(stockG) >= 0 ? '+' : ''}${stockG}%` : '--'}
                        </span>
                        {benchG && <span className="text-[10px] font-bold text-muted-foreground font-mono">S&P: {parseFloat(benchG) >= 0 ? '+' : ''}{benchG}%</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Analyst Revisions */}
          {hasEarnings && earningsRevisions.length > 0 && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
              <SectionHeader title="Analyst Revisions" />
              <div className="grid grid-cols-2 gap-2 mt-1">
                {earningsRevisions.slice(0, 2).map((r, i) => (
                  <div key={i} className="rounded-md border px-2.5 py-1.5" data-testid={`revision-${i}`}>
                    <p className="text-[10px] font-bold text-muted-foreground font-medium mb-1">{r.date_type === 'quarter' ? formatQtr(r.date) : `FY '${String(new Date(r.date).getFullYear()).slice(2)}`}</p>
                    <div className="flex items-center justify-between">
                      <div className="text-center">
                        <p className="text-base font-bold text-green-600 dark:text-green-400">{r.up_last30days ?? 0}</p>
                        <p className="text-[9px] text-muted-foreground">Up</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-bold text-red-600 dark:text-red-400">{r.down_last30days ?? 0}</p>
                        <p className="text-[9px] text-muted-foreground">Down</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fund Profile (ETFs / mutual funds) — surfaces the data we
              actually have (expense ratio, AUM, category, fund family,
              turnover, style, Morningstar rating). Only renders when at
              least one field is present so we never show an empty card. */}
          {hasFundProfile && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
              <div className="flex items-center gap-2 mt-4 mb-1.5" data-testid="fund-profile-header">
                <h4 className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">Fund Profile</h4>
                {fundProfile.expense_ratio != null && Number(fundProfile.expense_ratio) > 0.5 && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 cursor-help"
                    title={`Fee Drag — this fund's expense ratio of ${Number(fundProfile.expense_ratio).toFixed(2)}% is above 0.50%, eating into long-term returns versus low-cost index ETFs (typically 0.03%–0.20%).`}
                    data-testid="fund-profile-fee-drag-badge"
                  >
                    Fee Drag
                  </span>
                )}
                {fundProfile.expense_ratio != null && Number(fundProfile.expense_ratio) > 0 && Number(fundProfile.expense_ratio) <= 0.1 && (
                  <span
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 cursor-help"
                    title={`Low Fee — this fund's expense ratio of ${Number(fundProfile.expense_ratio).toFixed(2)}% is at or below 0.10%, the cost-efficient sweet spot for buy-and-hold index investing (think Vanguard, Schwab, iShares core funds).`}
                    data-testid="fund-profile-low-fee-badge"
                  >
                    Low Fee
                  </span>
                )}
              </div>
              {fundProfile.expense_ratio != null && (
                <MetricRow label="Expense Ratio" value={`${formatNum(fundProfile.expense_ratio)}%`} testId="m-fund-expense-ratio" defKey="EXPENSE_RATIO" />
              )}
              {fundProfile.expense_ratio != null && (
                <div className="text-[10px] text-muted-foreground -mt-1 mb-1 text-right" data-testid="m-fund-expense-ratio-source">
                  {fundProfile.expense_ratio_source === 'sec_rr'
                    ? `SEC prospectus fee table${fundProfile.expense_ratio_as_of ? ` · as of ${fundProfile.expense_ratio_as_of}` : ''}${fundProfile.expense_ratio_gross != null && Math.abs(fundProfile.expense_ratio_gross - fundProfile.expense_ratio) > 0.0001 ? ` · net of waivers (gross ${formatNum(fundProfile.expense_ratio_gross)}%)` : ''}`
                    : 'Yahoo Finance · fund files no SEC Risk/Return summary (grantor trust / UIT)'}
                </div>
              )}
              {fundProfile.net_assets != null && (
                <MetricRow label="Net Assets (AUM)" value={formatMoney(fundProfile.net_assets)} testId="m-fund-net-assets" defKey="NAV" />
              )}
              {fundProfile.net_assets != null && (
                <div className="text-[10px] text-muted-foreground -mt-1 mb-1 text-right" data-testid="m-fund-net-assets-source">
                  {fundProfile.net_assets_source === 'sec_nport'
                    ? `SEC N-PORT filing${fundProfile.net_assets_as_of ? ` · as of ${fundProfile.net_assets_as_of}` : ''}`
                    : 'Yahoo Finance · fund has no recent N-PORT (grantor trust / UIT)'}
                </div>
              )}
              {fundProfile.category && (
                <MetricRow label="Category" value={fundProfile.category} testId="m-fund-category" defKey="CATEGORY" />
              )}
              {fundProfile.fund_family && (
                <MetricRow label="Fund Family" value={fundProfile.fund_family} testId="m-fund-family" defKey="FUND_FAMILY" />
              )}
              {fundProfile.nav_price != null && (
                <MetricRow label="NAV" value={`$${formatNum(fundProfile.nav_price)}`} testId="m-fund-nav" defKey="NAV" />
              )}
              {fundProfile.turnover != null && (
                <MetricRow label="Portfolio Turnover" value={`${formatNum(fundProfile.turnover, 1)}%`} testId="m-fund-turnover" defKey="TURNOVER" />
              )}
              {(fundProfile.fund_size_style || fundProfile.fund_value_style) && (
                <MetricRow
                  label="Style"
                  value={[fundProfile.fund_size_style, fundProfile.fund_value_style].filter(Boolean).map((s2) => s2.charAt(0).toUpperCase() + s2.slice(1)).join(' / ')}
                  testId="m-fund-style"
                  defKey="STYLE"
                />
              )}
              {fundProfile.morningstar_rating != null && (
                <MetricRow
                  label="Morningstar"
                  value={`${'★'.repeat(Math.max(0, Math.min(5, Math.round(fundProfile.morningstar_rating))))}${'☆'.repeat(5 - Math.max(0, Math.min(5, Math.round(fundProfile.morningstar_rating))))}`}
                  testId="m-fund-ms-rating"
                  defKey="MORNINGSTAR"
                />
              )}
            </div>
          )}

          {/* ETF fund cards — Asset Allocation + Sector Breakdown +
              Top Holdings rendered together (Round 104e). Warmer
              holdings_data preferred, SEC N-PORT fills every gap. */}
          {(isFund || hasHoldings) && (
            <EtfTopHoldingsCard
              ticker={ticker}
              onSelectTicker={onOpenStock}
              warmAlloc={holdingsAlloc}
              warmSectors={holdingsSectors}
            />
          )}

          {/* Reverse N-PORT lookup — which ETFs hold this stock
              (Round 104e). Non-fund tickers only. */}
          {!isFund && <HeldByEtfsCard ticker={ticker} onSelectTicker={onOpenStock} />}

          {/* ETF/Fund Holdings */}
          {hasHoldings && (
            <>
              {holdingsValuation && Object.keys(holdingsValuation).length > 0 && (
                <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
                  <SectionHeader title="Portfolio Valuation" />
                  {Object.entries(holdingsValuation).map(([label, val]) => (
                    val != null && <MetricRow
                      key={label}
                      label={label}
                      value={formatNum(val)}
                      testId={`m-h-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      defKey={PORTFOLIO_VAL_DEFKEY[label] || null}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Analyst Coverage — Round 94a (Feb 2026) hybrid card:
              Finnhub OWNS recommendation, counts, Buy/Hold/Sell breakdown,
              6-month trend bars, and recent upgrade/downgrade actions.
              Yahoo OWNS target high/mean/low prices. If either upstream
              fails, the card still renders whatever the other one gave us.
              Replaces the legacy /api/stocks/{ticker}-sourced rows below
              (which only had stale seed-data because no live writer
              refreshes those fields anymore — see /admin/analyst-compare
              for the full rationale). */}
          <AnalystCoverageCard ticker={ticker} onNavigateWithOrigin={onNavigateWithOrigin} />

          {/* Profitability — Round P148·1 (Jun 2026): treasury-vehicle
              preferreds (SATA/STRK/STRD) get an explainer instead of
              the operating-company ratios, which GAAP mark-to-market
              noise renders meaningless (ROE -150%, ROIC -105%...). */}
          {stock?.is_treasury_vehicle ? (
            <div className="rounded-lg border bg-card px-4 py-3 break-inside-avoid" data-testid="m-profitability-treasury-note">
              <SectionHeader title="Profitability" />
              <p className="text-[11px] text-muted-foreground leading-snug mt-1">
                Not applicable — this is a treasury-vehicle preferred, not an operating company.
                GAAP mark-to-market on the sponsor&rsquo;s treasury assets distorts ROE, ROIC, margins
                and EPS growth into meaningless numbers. Judge it on par anchoring, the declaration
                record and sponsor leverage instead —{' '}
                <a href="/learn/treasury-vehicle-preferreds" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">see the guide</a>.
              </p>
            </div>
          ) : (isFiniteValue(s.profit_margins) || isFiniteValue(s.return_on_equity) || isFiniteValue(s.return_on_assets) || isFiniteValue(s.revenue_growth) || isFiniteValue(s.earnings_growth) || isFiniteValue(s.return_on_invested_capital) || isFiniteValue(s.free_cash_flow_margin)) && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
              <SectionHeader title="Profitability" />
              {isFiniteValue(s.return_on_invested_capital) && <MetricRow label="ROIC" value={formatPct(s.return_on_invested_capital)} testId="m-roic" defKey="ROIC" />}
              {isFiniteValue(s.free_cash_flow_margin) && <MetricRow label="FCF Margin" value={formatPct(s.free_cash_flow_margin)} testId="m-fcf-margin" defKey="FCF_MARGIN" />}
              {isFiniteValue(s.profit_margins) && <MetricRow label="Net Margin" value={formatPct(s.net_margin || s.profit_margins)} testId="m-profit-margin" defKey="NET_MARGIN" />}
              {isFiniteValue(s.return_on_equity) && <MetricRow label="ROE" value={formatPct(s.return_on_equity)} testId="m-roe" defKey="ROE" />}
              {isFiniteValue(s.return_on_assets) && <MetricRow label="ROA" value={formatPct(s.return_on_assets)} testId="m-roa" defKey="ROA" />}
              {isFiniteValue(s.revenue_growth) && <MetricRow label="Revenue Growth" value={formatPct(s.revenue_growth)} testId="m-rev-growth" defKey="REVENUE_GROWTH" />}
              {isFiniteValue(s.eps_growth) && <MetricRow label="EPS Growth (CAGR)" value={formatPct(s.eps_growth)} testId="m-eps-growth" defKey="EPS_GROWTH" />}
              {isFiniteValue(s.earnings_growth) && !isFiniteValue(s.eps_growth) && <MetricRow label="Earnings Growth" value={formatPct(s.earnings_growth)} testId="m-earn-growth" defKey="EPS_GROWTH" />}
              {isFiniteValue(s.revenue_stability) && <MetricRow label="Revenue Stability" value={formatNum(s.revenue_stability, 2)} testId="m-rev-stability" defKey="REVENUE_STABILITY" />}
            </div>
          )}

          {/* Financial Health */}
          {/* Round 95v (Jun 2026) — reads canonical `free_cash_flow`
              (legacy `free_cashflow` migrated out via
              scripts/migrate_free_cashflow_to_free_cash_flow.py). */}
          {(s.debt_to_equity != null || s.current_ratio != null || s.free_cash_flow != null || s.free_cashflow != null || s.total_revenue != null || s.total_debt != null || s.total_cash != null) && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
              <SectionHeader title="Financial Health" />
              {s.debt_to_equity != null && <MetricRow label="Debt/Equity" value={formatNum(s.debt_to_equity)} testId="m-debt-equity" defKey="DEBT_EQUITY" />}
              {s.current_ratio != null && <MetricRow label="Current Ratio" value={formatNum(s.current_ratio)} testId="m-current-ratio" defKey="CURRENT_RATIO" />}
              {(s.free_cash_flow ?? s.free_cashflow) != null && <MetricRow label="Free Cash Flow" value={formatMoney(s.free_cash_flow ?? s.free_cashflow)} testId="m-fcf" defKey="FREE_CASH_FLOW" />}
              {s.total_revenue != null && <MetricRow label="Total Revenue" value={formatMoney(s.total_revenue)} testId="m-revenue" defKey="TOTAL_REVENUE" />}
              {s.total_debt != null && <MetricRow label="Total Debt" value={formatMoney(s.total_debt)} testId="m-total-debt" defKey="TOTAL_DEBT" />}
              {s.total_cash != null && <MetricRow label="Total Cash" value={formatMoney(s.total_cash)} testId="m-total-cash" defKey="TOTAL_CASH" />}
            </div>
          )}

          {/* Ownership */}
          {(s.held_percent_institutions != null || s.held_percent_insiders != null || s.shares_outstanding != null || s.float_shares != null || s.short_ratio != null) && (
            <div className="rounded-lg border bg-card px-4 py-2 break-inside-avoid">
              <SectionHeader title="Ownership" />
              {s.held_percent_institutions != null && <MetricRow label="Institutional" value={`${(s.held_percent_institutions * 100).toFixed(1)}%`} testId="m-inst" defKey="INSTITUTIONAL" />}
              {s.held_percent_insiders != null && <MetricRow label="Insider" value={`${(s.held_percent_insiders * 100).toFixed(1)}%`} testId="m-insider" defKey="INSIDER" />}
              {s.shares_outstanding != null && <MetricRow label="Shares Outstanding" value={formatVolume(s.shares_outstanding)} testId="m-shares" defKey="SHARES_OUTSTANDING" />}
              {s.float_shares != null && <MetricRow label="Float" value={formatVolume(s.float_shares)} testId="m-float" defKey="FLOAT" />}
              {s.short_ratio != null && <MetricRow label="Short Ratio" value={formatNum(s.short_ratio)} testId="m-short-ratio" defKey="SHORT_RATIO" />}
            </div>
          )}
      </div>}

      {/* EPS Estimate Trend — full width below grid (needs table columns) */}
      {!chartOnly && hasEarnings && earningsTrend.length > 0 && (
        <div className="rounded-lg border bg-card px-4 py-3">
          <h4 className="text-[10px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400 mb-2">EPS Estimate Trend</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border/30">
                  <th className="text-left py-1 font-semibold text-muted-foreground">Period</th>
                  <th className="text-right py-1 font-semibold text-muted-foreground">Current</th>
                  <th className="text-right py-1 font-semibold text-muted-foreground">7d Ago</th>
                  <th className="text-right py-1 font-semibold text-muted-foreground">30d Ago</th>
                  <th className="text-right py-1 font-semibold text-muted-foreground">90d Ago</th>
                </tr>
              </thead>
              <tbody>
                {earningsTrend.slice(0, 4).map((t, i) => (
                  <tr key={i} className="border-b border-border/20">
                    <td className="py-1.5 font-medium">{t.date_type === 'quarter' ? formatQtr(t.date) : `FY '${String(new Date(t.date).getFullYear()).slice(2)}`}</td>
                    <td className="py-1.5 text-right font-mono font-semibold">{t.current?.toFixed(2) ?? '--'}</td>
                    <td className="py-1.5 text-right font-mono text-muted-foreground">{t.seven_days_ago?.toFixed(2) ?? '--'}</td>
                    <td className="py-1.5 text-right font-mono text-muted-foreground">{t.thirty_days_ago?.toFixed(2) ?? '--'}</td>
                    <td className="py-1.5 text-right font-mono text-muted-foreground">{t.ninety_days_ago?.toFixed(2) ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
